<template lang="pug">
a-button(
  type="primary"
  @click="doLogin()"
  ghost
  :disabled="loading"
  block
)
  .fa.mr-1(
    :class="getIconName()"
  )
  | {{buttonCaption}} »
  a-spin.ml-2(
    size="small"
    v-if="loading"
  )
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { toUpper, includes } from 'lodash';

import { projectIsTest, URLS } from '@/util';

import {
  doLoginIntoTmf,
  dologinIntoCrd,
  doLoginIntoIwrs,
  doLoginIntoIwrsLevel,
  doLoginIntoCmi,
  doLoginIntoErp,
  doLoginIntoEventsPlatform,
  doLoginIntoNexus,
  saveLogin,
} from '@/api';

export default {
  props: {
    project: {
      type: Object,
      default() {
        return {};
      },
    },
    // los proyectos cmi necesitan un crd
    // para obtener la "idcmi"
    crdProject: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapState('login', {
      instance: state => state.instance,
      token: state => state.token,
      username: state => state.user.username,
      password: state => state.password,
      userId: state => state.user.id,
      projectDbUsername: state => state.project.dbUsername,
    }),
    ...mapGetters('login', [
      'FORMATTED_TOKEN',
    ]),
    buttonCaption() {
      const PROJECT_IS_TEST = projectIsTest(this.project) && (
        this.project.type === 'CRD'
        || this.project.type === 'IWRS'
        || this.project.type === 'IWRS-LEVEL'
      );

      return `${toUpper(this.project.type)} ${PROJECT_IS_TEST ? '(TEST)' : ''}`;
    },
  },
  methods: {
    getIconName() {
      switch (this.project.type) {
        case 'CRD':
          return 'fa-book';
        case 'CMI':
          return 'fa-pie-chart';
        case 'IWRS':
        case 'IWRS-LEVEL':
          return 'fa-flask';
        case 'WEB':
          return 'fa-globe';
        case 'ERP':
          return 'fa-cubes';
        case 'EVENTS-PLATFORM':
          return 'fa-calendar-o';
        case 'NEXUS':
          return 'fa-cogs';
        case 'TMF':
          return 'fa-briefcase';
        default:
          return '';
      }
    },
    async saveLogin({ result }) {
      await saveLogin({
        token: this.FORMATTED_TOKEN,
        data: {
          userId: this.userId,
          username: this.username,
          projectId: this.project.id,
          profileId: this.project.profiler.profileEntity.id,
          result,
        },
      });
    },
    async doLogin() {
      this.loading = true;

      const REDIRECT_URL = encodeURIComponent(window.location.href);

      let location;

      try {
        switch (this.project.type) {
          case 'CRD':
            location = await dologinIntoCrd({
              instance: this.instance,
              projectId: this.project.id,
              token: this.token,
              username: this.username,
              profileId: this.project.profiler.profileEntity.id,
              organizationId: this.project.profiler.organizationId,
              redirectUrl: REDIRECT_URL,
            });
            break;
          case 'CMI':
            location = await doLoginIntoCmi({
              instance: this.instance,
              crdProjectDbUsername: this.crdProject.dbUsername,
              crdProjectId: this.crdProject.id,
              token: this.token,
              username: this.username,
              redirectUrl: REDIRECT_URL,
              userId: this.userId,
              projectTitle: this.project.profiler.projectEntity.name,
            });
            break;
          case 'IWRS':
            location = await doLoginIntoIwrs({
              instance: this.instance,
              profilerId: this.project.profiler.id,
              userId: this.userId,
              token: this.token,
              redirectUrl: REDIRECT_URL,
            });
            break;
          case 'IWRS-LEVEL':
            location = await doLoginIntoIwrsLevel({
              instance: this.instance,
              profilerId: this.project.profiler.id,
              userId: this.userId,
              token: this.token,
              redirectUrl: REDIRECT_URL,
            });
            break;
          case 'ERP':
            location = await doLoginIntoErp({
              profilerId: this.project.profiler.id,
              userId: this.userId,
              token: this.token,
              redirectUrl: REDIRECT_URL,
            });
            break;
          case 'EVENTS-PLATFORM':
            location = await doLoginIntoEventsPlatform({
              userId: this.userId,
              token: this.token,
              redirectUrl: REDIRECT_URL,
            });
            break;
          case 'NEXUS':
            location = await doLoginIntoNexus({
              instance: this.instance,
              profilerId: this.project.profiler.id,
              token: this.token,
              redirectUrl: REDIRECT_URL,
            });
            break;
          case 'TMF':
            location = await doLoginIntoTmf({
              instance: this.instance,
              profilerId: this.project.profiler.id,
              token: this.token,
              redirectUrl: REDIRECT_URL,
            });
            break;
          default:
            location = URLS.defaultRedirect;
            break;
        }

        if (includes(['CMI', 'IWRS', 'ERP', 'EVENTS-PLATFORM'], this.project.type)) {
          await this.saveLogin({ result: 'OK' });
        }

        if (!location) {
          return;
        }

        if (this.project.type === 'CMI') {
          const win = window.open(location, '_blank');
          win.focus();
        } else {
          window.location = location;
        }
      } catch (error) {
        const { response } = error;
        const { status } = response;

        if (includes(['CMI', 'IWRS', 'ERP', 'EVENTS-PLATFORM'], this.project.type)) {
          await this.saveLogin({ result: status });
        }

        this.$message.error(`
          ${this.$t('components.project_login_button.js.access_error_message_1')} "${this.project.name}".
          ${this.$t('components.project_login_button.js.access_error_message_2')}
        `, 2);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
